import React from "react";
import { toast } from "sonner";
import { Save, UploadCloud, RotateCcw, FileText, Plus, Trello, Calendar, CalendarClock, LifeBuoy, Headphones, ShoppingBag } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button, Spinner, Skeleton } from "@/components/ui";

const INTEGRATIONS = {
  trello: { name: "Trello", Icon: Trello },
  google_calendar: { name: "Google Calendar", Icon: Calendar },
  calendly: { name: "Calendly", Icon: CalendarClock },
  zendesk: { name: "Zendesk", Icon: LifeBuoy },
  zoho_desk: { name: "Zoho Desk", Icon: Headphones },
  shopify: { name: "Shopify", Icon: ShoppingBag },
  jotform: { name: "JotForm", Icon: FileText },
};
const TOKEN_RE = /@(trello|google_calendar|calendly|zendesk|zoho_desk|shopify|jotform)\b/g;

function IntegrationPill({ id }) {
  const meta = INTEGRATIONS[id];
  if (!meta) return <span>@{id}</span>;
  const { name, Icon } = meta;
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 mx-0.5 align-baseline" data-testid={`pb-pill-${id}`}
      style={{ background: "rgba(170,219,60,0.12)", border: "1px solid var(--accent)", color: "var(--accent)", fontSize: "0.78em", borderRadius: 2, fontFamily: "'IBM Plex Sans', sans-serif" }}>
      <Icon size={12} /> {name}
    </span>
  );
}

export function renderWithPills(text) {
  const out = [];
  let last = 0, m, key = 0;
  TOKEN_RE.lastIndex = 0;
  while ((m = TOKEN_RE.exec(text)) !== null) {
    if (m.index > last) out.push(<span key={key++}>{text.slice(last, m.index)}</span>);
    out.push(<IntegrationPill key={key++} id={m[1]} />);
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(<span key={key++}>{text.slice(last)}</span>);
  return out;
}


const TEMPLATE = `## Tone
- Friendly, concise, and direct
- Never promise a final price or availability until the team confirms

## When a customer asks to book a service
- Ask for the details we need first:
  - Service type
  - City or area
  - Preferred date
- Check @google_calendar for a free slot
- Then log it as a new request in @trello
- If a request already exists, update that one instead of creating another

## Complaints
- Treat as **high priority**
- Route to @trello and hand off to a person straight away

## When someone asks for a human
- Hand off to the team
`;

export default function PlaybookEditor({ businessId, onPublished, withRail = true, hideActions = false, onState }) {
  const [status, setStatus] = React.useState(null);
  const [text, setText] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [publishing, setPublishing] = React.useState(false);
  const [versions, setVersions] = React.useState([]);
  const [selVer, setSelVer] = React.useState(null);
  const MAX = 12000;
  const taRef = React.useRef(null);

  const insertToken = (id) => {
    const ta = taRef.current;
    const token = `@${id} `;
    const start = ta ? ta.selectionStart : text.length;
    const end = ta ? ta.selectionEnd : text.length;
    const next = text.slice(0, start) + token + text.slice(end);
    setText(next.slice(0, MAX));
    requestAnimationFrame(() => {
      if (!ta) return;
      ta.focus();
      const pos = start + token.length;
      ta.setSelectionRange(pos, pos);
    });
  };

  const loadAll = React.useCallback(async () => {
    const [s, v] = await Promise.all([
      api.get(`/businesses/${businessId}/playbook`),
      api.get(`/businesses/${businessId}/playbook/versions`),
    ]);
    setStatus(s.data);
    setText(s.data.draft?.text || "");
    setVersions(v.data);
  }, [businessId]);

  React.useEffect(() => { loadAll(); }, [loadAll]);

  const saveDraft = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/businesses/${businessId}/playbook/draft`, { text });
      if (data.secretRedacted) { setText(data.text); toast.warning("A possible secret was detected and removed."); }
      else toast.success("Saved");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const publish = async () => {
    if (!text.trim()) return;
    setPublishing(true);
    try {
      const v = await api.post(`/businesses/${businessId}/playbook/validate`, { text });
      const { canPublish, findings, validationId, contentHash } = v.data;
      if (!canPublish) {
        const blocker = (findings || []).find((f) => f.severity === "blocked" || f.severity === "needs_clarification");
        toast.error(blocker ? blocker.message : "Please fix the highlighted issues before publishing.");
        return;
      }
      const pub = await api.post(`/businesses/${businessId}/playbook/publish`, {
        validationId, contentHash, reviewAcknowledged: true,
      });
      toast.success(`Published version ${pub.data.version}`);
      await loadAll();
      onPublished?.(pub.data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setPublishing(false); }
  };

  const rollback = async (versionId) => {
    try {
      await api.post(`/businesses/${businessId}/playbook/rollback`, { versionId });
      toast.success("Rolled back as a new version");
      await loadAll();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const openVersion = async (v) => {
    setSelVer({ version: v.version, publishedAt: v.publishedAt, isActive: v.isActive, loading: true });
    try {
      const { data } = await api.get(`/businesses/${businessId}/playbook/versions/${v.id}`);
      setSelVer({ version: v.version, publishedAt: v.publishedAt, isActive: v.isActive, rawText: data.rawText || "" });
    } catch (e) { toast.error("Couldn't load these instructions"); setSelVer(null); }
  };

  const active = status?.activeVersion;

  const Rail = (
    <aside data-testid="pb-rail">
      <button className={`nav-link w-full text-left ${!selVer ? "active" : ""}`} data-testid="rail-draft" onClick={() => setSelVer(null)}>
        <FileText size={16} strokeWidth={1.6} style={{ flexShrink: 0 }} />
        <span className="flex-1">Draft</span>
        <span className="mono text-[10px]" style={{ color: "var(--text-muted)" }}>editing</span>
      </button>
      {versions.map((v) => (
        <div key={v.id} className="group relative">
          <button className={`nav-link w-full text-left ${selVer?.version === v.version ? "active" : ""}`}
            data-testid={`rail-version-${v.version}`} onClick={() => openVersion(v)}>
            <span className="mono text-sm">v{v.version}</span>
            {v.isActive && <span className="mono text-[10px]" style={{ color: "var(--accent)" }}>live</span>}
            <span className="mono text-[10px] ml-auto pr-5" style={{ color: "var(--text-muted)" }}>{new Date(v.publishedAt).toLocaleDateString()}</span>
          </button>
          {!v.isActive && (
            <button onClick={(e) => { e.stopPropagation(); rollback(v.id); }} title="Roll back" data-testid={`rollback-${v.version}`}
              className="absolute right-3 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: "var(--text-muted)" }}>
              <RotateCcw size={13} />
            </button>
          )}
        </div>
      ))}
      {versions.length === 0 && <div className="px-4 text-xs py-1" style={{ color: "var(--text-muted)" }}>No published versions yet.</div>}
    </aside>
  );

  const VersionView = selVer && (
    <div className="flex-1 min-w-0" data-testid="version-view">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2 text-sm" style={{ color: "var(--text-2)" }}>
          <FileText size={15} style={{ color: "var(--accent-2)" }} />
          Version <span className="mono" style={{ color: "var(--text)" }}>v{selVer.version}</span>
          {selVer.isActive && <span className="mono text-[10px]" style={{ color: "var(--accent)" }}>live</span>}
          <span className="mono text-[11px]" style={{ color: "var(--text-muted)" }}>· {new Date(selVer.publishedAt).toLocaleDateString()}</span>
        </div>
        <Button size="sm" onClick={() => setSelVer(null)} data-testid="version-back">Back to draft</Button>
      </div>
      <p className="text-xs mb-2" style={{ color: "var(--text-muted)" }}>The instructions published in this version (read-only).</p>
      <div style={{ background: "var(--s1)" }}>
        {selVer.loading ? (
          <div className="p-6"><Spinner /></div>
        ) : (
          <pre className="w-full px-4 py-3 mono text-sm" data-testid="version-text"
            style={{ color: "var(--text)", whiteSpace: "pre-wrap", wordBreak: "break-word", minHeight: 460, lineHeight: 1.7, margin: 0 }}>
            {selVer.rawText || "(No instructions stored for this version.)"}
          </pre>
        )}
      </div>
    </div>
  );

  const Body = (
    <div className="flex-1 min-w-0">
      {/* status bar */}
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2 text-sm" style={{ color: "var(--text-2)" }}>
          <FileText size={15} style={{ color: "var(--accent-2)" }} />
          {active ? <>Live: <span className="mono" style={{ color: "var(--text)" }}>v{active.version}</span> · {active.ruleCount} rules</> : <span>Not published yet</span>}
        </div>
        {!hideActions && (
          <div className="flex gap-2">
            <Button size="sm" onClick={saveDraft} disabled={saving} data-testid="save-draft-btn">
              {saving ? <Spinner size={14} /> : <Save size={14} />} Save
            </Button>
            <Button variant="accent" size="sm" onClick={publish} disabled={publishing || !text.trim()} data-testid="publish-btn">
              {publishing ? <><Spinner size={14} /> Publishing…</> : <><UploadCloud size={14} /> Publish</>}
            </Button>
          </div>
        )}
      </div>

      {!text && (
        <p className="text-sm mb-3" style={{ color: "var(--text-muted)" }}>
          <button className="underline" style={{ color: "var(--accent-2)" }} onClick={() => setText(TEMPLATE)} data-testid="insert-template">
            Start from a template
          </button>
        </p>
      )}

      {/* insert integrations */}
      <div className="flex items-center gap-2 flex-wrap mb-2.5" data-testid="pb-integration-toolbar">
        <span className="text-xs flex items-center gap-1" style={{ color: "var(--text-muted)" }}><Plus size={12} /> Add integration:</span>
        {Object.entries(INTEGRATIONS).map(([id, meta]) => {
          const Icon = meta.Icon;
          return (
            <button key={id} type="button" onClick={() => insertToken(id)} data-testid={`pb-insert-${id}`}
              className="inline-flex items-center gap-1.5 px-2 py-1 text-xs transition-colors"
              style={{ background: "var(--s2)", border: "1px solid var(--border)", color: "var(--text-2)" }}>
              <Icon size={13} /> {meta.name}
            </button>
          );
        })}
      </div>

      {/* editor */}
      <div style={{ background: "var(--s1)" }}>
        <textarea
          ref={taRef}
          value={text} maxLength={MAX} onChange={(e) => setText(e.target.value)} data-testid="playbook-editor"
          spellCheck={false}
          className="w-full px-4 py-3 mono text-sm"
          style={{ background: "var(--s1)", color: "var(--text)", border: "none", outline: "none", resize: "vertical", minHeight: 300, lineHeight: 1.7 }}
          placeholder={"## Complaints\n- Treat as high priority\n- Route to @trello\n\n## When a customer asks to book\n- Ask for: service, city, date\n- Check @google_calendar for a free slot\n- Then log a new request in @trello"}
        />
      </div>

      <div className="flex items-center justify-between mt-2">
        <span className="mono text-xs" style={{ color: text.length > MAX * 0.9 ? "var(--warning)" : "var(--text-muted)" }} data-testid="char-count">{text.length} / {MAX}</span>
        <span className="text-xs" style={{ color: "var(--text-muted)" }}>Don't paste passwords or API keys.</span>
      </div>

      {/* live preview with integration pills */}
      {text.trim() && (
        <div className="mt-4" data-testid="pb-preview">
          <div className="heading-eyebrow mb-1.5">Preview</div>
          <div className="px-4 py-3 text-sm" data-testid="pb-preview-body"
            style={{ background: "var(--s2)", border: "1px solid var(--border)", color: "var(--text-2)", whiteSpace: "pre-wrap", lineHeight: 1.8, wordBreak: "break-word" }}>
            {renderWithPills(text)}
          </div>
          <p className="text-xs mt-1.5" style={{ color: "var(--text-muted)" }}>Connect each integration under the Integrations tab so Wappy can act on it.</p>
        </div>
      )}
    </div>
  );

  const loading = status === null;
  const railEl = loading ? (
    <aside data-testid="pb-rail">
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex items-center gap-2 px-3 py-2.5">
          <Skeleton style={{ width: 16, height: 16 }} />
          <Skeleton className="flex-1" style={{ height: 12, maxWidth: 110 }} />
        </div>
      ))}
    </aside>
  ) : Rail;
  const bodyEl = loading ? (
    <div className="flex-1 min-w-0">
      <Skeleton style={{ height: 16, width: 180, marginBottom: 20 }} />
      <Skeleton style={{ height: 460 }} />
    </div>
  ) : (selVer ? VersionView : Body);

  React.useEffect(() => {
    onState?.({ saveDraft, publish, saving, publishing, hasText: !!text.trim(), rail: railEl, viewingVersion: !!selVer });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saving, publishing, text, status, versions, selVer]);

  if (!withRail) return bodyEl;
  return (
    <div className="flex flex-col lg:flex-row gap-8">
      <div className="lg:w-52 flex-shrink-0 lg:pr-7 lg:border-r">{railEl}</div>
      <div className="flex-1 min-w-0">{bodyEl}</div>
    </div>
  );
}
